import { SingingPortraitFragment } from 'api/contentful/contentful.graphql.types'
import { animateUserMemory, postUserMemory, putReferralMemoryUuid } from 'api/goodtrust/user/memory'
import classNames from 'classnames'
import { Block } from 'components/Block'
import { MemoryVideo } from 'components/content/memory/memoryVideo/MemoryVideo'
import { isPictureTooBig, MEMORY_SIZE_LIMIT } from 'components/content/memory/utils'
import { useChooseSongModal } from 'components/content/singingPortraits/chooseSongModal/ChooseSongModal'
import { CreateSPActions } from 'components/content/singingPortraits/headerActions/CreateSPActions'
import { SelectFromGalleryModal } from 'components/content/singingPortraits/selectFromGalleryModal/SelectFromGalleryModal'
import { SelectFromGalleryModalActions } from 'components/content/singingPortraits/selectFromGalleryModal/SelectFromGalleryModalActions'
import { License } from 'components/content/singingPortraits/singingPortraitsHero/License'

import {
  mediaModalCss,
  SContentBox,
  SDescription,
  SDragDropTextContainer,
  ShareItemsContainer,
  SImg,
  SInstructiveText,
  SLeftContainer,
  SOuterContainer,
  SRightContainer,
  SUploadingWrapper,
} from './SingingPortraitsHero.styled'
import { useTakePhotoModal } from 'components/content/singingPortraits/takePhotoModal/TakePhotoModal'
import { useFakeProgressBar } from 'components/fakeProgressBar/FakeProgressBar'
import { usePaywallMemory } from 'components/memory/MemoryPaywallUtils'
import { SelectedMemory } from 'components/memory/MemoryUtils'
import { useAuthModal } from 'components/modal/authModal/AuthModal'
import { useModal } from 'components/modal/Modal'
import { handleAndToastError, toastError, toastErrorString, toastSuccess } from 'components/Toast'
import { Text, Title, Title_h2_default } from 'components/Typography'
import { VideoCarousel } from 'components/videoCarousel/VideoCarousel'
import { useTranslation } from 'next-i18next'
import Router from 'next/router'
import React, { ReactElement, useMemo, useState } from 'react'
import { FileRejection, useDropzone } from 'react-dropzone'
import { fireEvent } from 'utils/analytics'
import { SingingSourcePhoto } from 'utils/analytics/types'
import { ApiError } from 'utils/error'
import { unwrapResponse } from 'utils/fetcher'
import { describeFileRejections, useFileWithObjectUrl } from 'utils/file'
import { getIsMobile, logError } from 'utils/general'
import { useLoadingCallback } from 'utils/hooks'
import { useStoredPromoCodeLogic } from 'utils/promo'
import {
  clearSessionValue,
  getSessionValue,
  SESSION_REFERRAL_MEMORY_UUID,
} from 'utils/sessionStorage'
import { GenericAsyncFunction } from 'utils/types'

interface FakeFile {
  name: string
}

export function SingingPortraitsHero(props: {
  title: string
  desc?: string
  extraActions?: ReactElement
  showDefaultActionsAtBottom?: boolean
  shareItems?: ReactElement
  memories?: SelectedMemory[]
  previewAnimations?: SingingPortraitFragment[]
  isPublic?: boolean
  className?: string
  hideLicense?: boolean
  hideDragDrop?: boolean
  showLicenseBelowImage?: boolean
  onChange?: GenericAsyncFunction
}) {
  const { t } = useTranslation('memory')
  const storedPromoLogic = useStoredPromoCodeLogic()
  const [takenSelfie, setTakenSelfie] = useState<FakeFile>()

  const [authModal, authWithModal, closeAuthModal] = useAuthModal({
    config: {
      buttonTitle: 'Continue & finish animation',
      analyticsData: { type: 'singing_portraits' },
      source: 'LIFE_STORY',
    },
    onLogin: () => fireEvent('sh_signup', { action: 'logged_in' }),
    onSignup: async () => {
      fireEvent('sh_signup', { action: 'signed_up' })
      try {
        const referralMemoryUuid = getSessionValue(SESSION_REFERRAL_MEMORY_UUID)
        if (referralMemoryUuid) {
          await putReferralMemoryUuid(referralMemoryUuid).then(unwrapResponse)
          clearSessionValue(SESSION_REFERRAL_MEMORY_UUID)
        }
      } catch (e) {
        logError('Memory referral failed: ', e)
      }
    },
    onCancel: () => {
      fireEvent('sh_signup', { action: 'aborted' })
      setTimeout(() => window.location.reload())
    },
  })

  const [takePhotoModal, openTakePhotoModal, closeTakePhotoModal] = useTakePhotoModal((photo) => {
    const name = 'selfie.jpeg'

    closeTakePhotoModal()
    setTakenSelfie({
      name,
    })

    void upload(photo, 'selfie')
  })

  const [chooseSongModal, chooseSongViaModal] = useChooseSongModal()

  const { hitMemoryPaywallWithRedirect } = usePaywallMemory()

  const [fakeProgressBar, progressFakeProgressBar] = useFakeProgressBar()

  const { url: uploadedImageUrl, setFile: setUploadedImage } = useFileWithObjectUrl()

  const [upload, isUploading, wasUploading] = useLoadingCallback(
    async (file: File, source?: SingingSourcePhoto) => {
      fireEvent('sh_photo_uploaded', {
        page: 'singing_landing',
        photo_source: source || 'file_upload',
      })
      setUploadedImage(file)
      await progressFakeProgressBar(50, 4000)
      authWithModal(async () => {
        closeAuthModal()
        progressFakeProgressBar(70, 2000)

        await storedPromoLogic?.onRegistered()

        try {
          const uploadRes = await postUserMemory(file).then(unwrapResponse)
          if (!uploadRes.json?.uuid) throw new ApiError(uploadRes)

          const driver = await chooseSongViaModal()
          if (!driver) {
            toastSuccess('animation_cancelled')
            return
          }

          progressFakeProgressBar(90, 2000)

          const animateRes = await animateUserMemory(uploadRes.json.uuid, { driver_type: driver })
          if (!animateRes.ok) {
            if (animateRes.errorCode === 'user_exceeded_free_quota') {
              await progressFakeProgressBar(0, 500)
              await hitMemoryPaywallWithRedirect({
                memoryUuid: uploadRes.json.uuid,
                driver,
              })
              return
            } else {
              throw new ApiError(animateRes)
            }
          }
          await progressFakeProgressBar(100, 1000)
          await Router.push(`/singing-portraits/${uploadRes.json.uuid}/animating`)
        } catch (err) {
          await progressFakeProgressBar(0, 500)
          handleAndToastError(err)
        }
      })
    }
  )

  const { getRootProps, getInputProps, isDragActive, inputRef, acceptedFiles } = useDropzone({
    maxSize: MEMORY_SIZE_LIMIT,
    accept: 'image/png,image/jpeg',
    multiple: false,
    noClick: true,
    onDrop: async (acceptedFiles: File[], fileRejections: FileRejection[]) => {
      if (fileRejections.length > 0) {
        toastErrorString(describeFileRejections(fileRejections))
      } else if (acceptedFiles.length) {
        const file = acceptedFiles[0]
        if (file) {
          isPictureTooBig(file).then((isTooBig) => {
            if (isTooBig) {
              toastError('image_10mp_limit')
            } else {
              upload(file)
            }
          })
        }
      }
    },
  })

  const [mediaModal, openMediaModal, closeMediaModal] = useModal(
    ({ close, isOpen }) =>
      isOpen ? (
        <SelectFromGalleryModal
          mode="single"
          defaultValue={
            props.memories?.[0] ? [{ ...props.memories[0], selectedAnimation: null }] : []
          }
          onMemoriesSelected={async ([selectedMemory]) => {
            if (!selectedMemory || !selectedMemory.uuid) return
            const memoryUuid = selectedMemory.uuid

            try {
              const driver = await chooseSongViaModal()

              fireEvent('sh_photo_uploaded', { page: 'singing_ready', photo_source: 'library' })

              if (!driver) {
                toastSuccess('animation_cancelled')
                return
              }

              const animateRes = await animateUserMemory(memoryUuid, { driver_type: driver })
              if (!animateRes.ok) {
                if (animateRes.errorCode === 'user_exceeded_free_quota') {
                  await hitMemoryPaywallWithRedirect({
                    memoryUuid,
                    driver,
                  })
                  return
                } else {
                  throw new ApiError(animateRes)
                }
              }
              await props.onChange?.()
              await Router.push(`/singing-portraits/${memoryUuid}/animating`)
            } catch (err) {
              handleAndToastError(err)
            }
          }}
          onClose={close}
          actions={
            <SelectFromGalleryModalActions
              onTakeSelfiePress={() => {
                closeMediaModal()
                onTakeSelfiePress()
              }}
              onUploadPhotoPress={() => {
                closeMediaModal()
                onUploadPhotoPress()
              }}
            />
          }
        />
      ) : null,
    {
      modalCss: mediaModalCss,
    }
  )

  const acceptedFilesIncludingSelfie: FakeFile[] = useMemo(() => {
    return takenSelfie ? [takenSelfie] : acceptedFiles
  }, [acceptedFiles, takenSelfie])

  const onTakeSelfiePress = () => {
    const isMobile = getIsMobile()

    if (!isMobile) {
      openTakePhotoModal()
      return
    }

    document.getElementById('photo-upload-button')?.setAttribute('capture', 'user')
    document.getElementById('photo-upload-button')?.click()
  }

  const onUploadPhotoPress = () => {
    document.getElementById('photo-upload-button')?.removeAttribute('capture')
    document.getElementById('photo-upload-button')?.click()
  }

  return (
    <>
      {authModal}
      {takePhotoModal}
      {chooseSongModal}
      {mediaModal}
      {storedPromoLogic?.jsx}
      <input
        ref={inputRef}
        {...getInputProps()}
        id="photo-upload-button"
        data-testid="upload-photo"
      />

      <Block className={props.className}>
        <SOuterContainer>
          <SLeftContainer
            {...getRootProps({
              isDragActive,
              isAccepting: !isUploading && !(wasUploading || !!takenSelfie),
            })}
          >
            <SContentBox>
              <Title variant={Title_h2_default}>{props.title}</Title>
              {!!props.desc && <SDescription>{props.desc}</SDescription>}

              {!props.showDefaultActionsAtBottom && (
                <CreateSPActions
                  onTakeSelfiePress={onTakeSelfiePress}
                  onUploadPhotoPress={onUploadPhotoPress}
                />
              )}

              <SDragDropTextContainer className="desktop-only">
                {!props.hideDragDrop && (
                  <SInstructiveText>{t('memory.singing.drag_drop_instruction')}</SInstructiveText>
                )}

                {!props.hideLicense && !props.showLicenseBelowImage && <License isAbsolute />}
              </SDragDropTextContainer>
            </SContentBox>
            <ShareItemsContainer className="desktop-only">{props.shareItems}</ShareItemsContainer>
            <div className="desktop-only">{props.extraActions}</div>
          </SLeftContainer>

          <SRightContainer isUploading={wasUploading || !!takenSelfie || isUploading}>
            {wasUploading || !!takenSelfie || isUploading ? (
              <SUploadingWrapper>
                {uploadedImageUrl && <SImg src={uploadedImageUrl} />}
                {fakeProgressBar}
                <Text css={{ fontWeight: 'bold', color: 'black' }}>
                  {t('memory.singing.uploading_info_text')}
                </Text>
                <Text>{acceptedFilesIncludingSelfie[0]?.name}</Text>
              </SUploadingWrapper>
            ) : props.previewAnimations ? (
              <VideoCarousel items={props.previewAnimations} />
            ) : (
              <MemoryVideo
                onChangePhotoOrSong={openMediaModal}
                memory={props.memories?.[0]}
                isPublic={props.isPublic}
              />
            )}

            <License className={classNames(!props.showLicenseBelowImage && 'mobile-only')} />
          </SRightContainer>
        </SOuterContainer>
      </Block>

      <Block className="mobile-only">
        <div>
          <ShareItemsContainer>{props.shareItems}</ShareItemsContainer>
          {props.extraActions}
          {props.showDefaultActionsAtBottom && (
            <CreateSPActions
              onTakeSelfiePress={onTakeSelfiePress}
              onUploadPhotoPress={onUploadPhotoPress}
            />
          )}
        </div>
      </Block>
    </>
  )
}
